import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Avatar,
  Divider,
  Snackbar,
  CircularProgress,
  Container,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  Checkbox,
  FormGroup,
  Dialog,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import FileUpload from "components/File Upload/FileUpload";
import ErrorDisplay from "components/Error display/NoFormStructure";
import { CloudUpload } from "bootstrap-icons-react";
import { AttachFile, DriveFileRenameOutline, Note } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { CameraIcon } from "lucide-react";
import GlobalCityAutocomplete from "components/CitiesPicker/CitiesPicker";
import new_logo from "../../assets/img/new_logo.svg";
import { useTranslation } from "react-i18next";
import { OutlinedButton } from "components/Buttons/Buttons";
import SkeletonLoaderDemo from "components/Loading/LoadingDemo";
import { ModernLoader } from "components/Loading/LoadingDots";
const StyledSelect = styled(Select)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "20px",
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#0017E7",
      borderWidth: "1px",
    },
    "&:hover": {
      transition: "0.3s ease",
      boxShadow: "none",
    },
    "&.Mui-focused": {
      boxShadow: "none",
      borderColor: "#0017E7",
      borderWidth: "2px",
      "& fieldset": {
        borderColor: "transparent",
        borderWidth: "2px",
      },
    },
    "&.Mui-error": {
      "&:hover, &.Mui-focused": {
        boxShadow: "none",
        borderColor: "#d32f2f",
      },
    },
  },
}));
const UploadCard = styled(Card)(({ theme, isDragging, isUploaded }) => ({
  width: "100%",
  height: "100%",
  minHeight: "200px",
  borderRadius: "16px",
  boxShadow: "none",
  transition: "all 0.3s ease",
  backgroundColor: isUploaded
    ? "rgba(255, 255, 255, 0.3)"
    : "rgba(255, 255, 255, 0.2)",
  backdropFilter: "blur(10px)",
  border: "2px dashed",
  borderColor: isDragging
    ? theme.palette.primary.main
    : isUploaded
    ? "rgba(0, 0, 0, 0.4)"
    : "rgba(224, 224, 224, 0.6)",
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  transform: isDragging ? "scale(1.02)" : "scale(1)",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 6px 12px rgba(0,0,0,0.1)",
    backgroundColor: "rgba(255, 255, 255, 0.35)",
  },
}));

const HiddenInput = styled("input")({
  display: "none",
});

const IconWrapper = styled(Box)(({ theme, isUploaded }) => ({
  "& .MuiSvgIcon-root": {
    fontSize: 48,
    marginBottom: theme.spacing(2),
    color: isUploaded ? "rgba(0, 0, 0, 0.7)" : "rgba(158, 158, 158, 0.8)",
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  overflow: "visible",
  background: "#ffffff",
  boxShadow: "none",
  borderRadius: "20px",
  border: "1.5px solid #D9D9D9",
  // transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
  "&:hover": {
    // transform: "translateY(-5px)",
    boxShadow: "none",
  },
}));

const CompanyInfoCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  overflow: "visible",
  background: "#ffffff",
  boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.1)",
  borderRadius: "20px",
  border: "1px solid #f0f0f0",
}));

const AvatarWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  marginBottom: theme.spacing(2),
  "&::after": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "110%",
    height: "110%",
    borderRadius: "50%",
    background: "#f5f5f5",
    zIndex: -1,
  },
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
  border: "4px solid #fff",
}));

const FormSectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.h5.fontSize,
  fontWeight: 600,
  color: "#333",
  marginBottom: theme.spacing(3),
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: "-8px",
    left: 0,
    width: "40px",
    height: "4px",
    backgroundColor: "#333",
    borderRadius: "2px",
  },
}));

const InfoItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(1),
  "& > svg": {
    marginRight: theme.spacing(1),
    color: "#555",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    fontFamily: "Poppins",
    borderColor: "#00000033",
    backgroundColor: "#fff",
    transition: "border-color 0.4s ease, box-shadow 0.4s ease",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#e0e0e0",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      boxShadow: "0px 4px 20px rgba(0, 23, 231, 0.1)",
      borderColor: "#0017E7",
      transition: "all 0.3s ease",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      boxShadow: "0px 4px 20px rgba(0, 23, 231, 0.1)",
      borderColor: "#0017E7",
      transition: "all 0.3s ease",
    },
    "& .MuiOutlinedInput-input": {
      minHeight: "36px",
      padding: "8px 14px",
    },
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: "30px",
  padding: "12px 30px",
  background: "#333",
  color: "#fff",
  boxShadow: "0 3px 5px 2px rgba(0, 0, 0, .1)",
  transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
  "&:hover": {
    background: "#555",
    transform: "scale(1.05)",
    boxShadow: "0 6px 10px 4px rgba(0, 0, 0, .15)",
  },
}));

const UploadButton = styled(Button)(({ theme }) => ({
  borderRadius: "12px",
  padding: "10px 20px",
  background: "#f5f5f5",
  color: "#333",
  border: "2px dashed #ccc",
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    background: "#e0e0e0",
    borderColor: "#999",
  },
}));

const FileNameDisplay = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: "5px 10px",
  background: "#f0f0f0",
  borderRadius: "5px",
  fontSize: "0.875rem",
  color: "#555",
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  cursor: "pointer",
  "& img": {
    width: "120px",
    height: "auto",
    transition: "transform 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
    },
  },
}));

const FormHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(3),
  position: "relative",
}));
const FileUploadCard = ({ type, isUploaded, register, handleFileUpload, required = false }) => {
  const [isDragging, setIsDragging] = useState(false);
  const { t } = useTranslation();

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragIn = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOut = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const syntheticEvent = {
        target: {
          files: files,
        },
      };
      handleFileUpload(
        syntheticEvent,
        type === "resume" ? "resume" : "coverLetter"
      );
    }
  };

  const isResume = type === "resume";
  const Icon = isResume ? AttachFile : Note;
  const title = isResume ? t("applicationForm.resume") : t("applicationForm.coverLetter");
  const subtitle = isResume
    ? t("form.acceptedFormats")
    : t("spontaneous.motivation");

  return (
    <Grid item xs={6}>
      <HiddenInput
        id={`${type}-upload`}
        type="file"
        {...register(type === "resume" ? "resume" : "cover_letter", {
          required: required,
          onChange: (e) =>
            handleFileUpload(e, type === "resume" ? "resume" : "coverLetter"),
        })}
      />
      <label
        htmlFor={`${type}-upload`}
        style={{ display: "block", height: "100%" }}
        onDragEnter={handleDragIn}
        onDragLeave={handleDragOut}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <UploadCard isDragging={isDragging} isUploaded={isUploaded}>
          <CardContent sx={{ padding: "24px", textAlign: "center" }}>
            <IconWrapper isUploaded={isUploaded}>
              <Icon />
            </IconWrapper>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isUploaded
                  ? "rgba(0, 0, 0, 0.87)"
                  : "rgba(0, 0, 0, 0.7)",
                mb: 1,
              }}
            >
              {isDragging
                ? t("form.dropHere")
                : isUploaded
                ? `${title} ${t("applicationForm.coverLetter")}`
                : `${t("")} ${title}`}
              {required && (
                <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
              )}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: required && !isUploaded ? "rgba(244, 67, 54, 0.8)" : "rgba(0, 0, 0, 0.6)",
                mt: 1,
              }}
            >
              {isDragging 
                ? t("form.releaseToUpload") 
                : required && !isUploaded 
                ? "Required" 
                : subtitle}
            </Typography>
          </CardContent>
        </UploadCard>
      </label>
    </Grid>
  );
};

const ApplicationFormSp = () => {
  const [formData, setFormData] = useState({});
  const [formStructure, setFormStructure] = useState([]);
  const [adminId, setAdminId] = useState(null);
  const [description, setDescription] = useState(null);
  const [adminData, setAdminData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { companyName } = useParams();
  const [files, setFiles] = useState({});
  const apiUrl = process.env.REACT_APP_API_URL;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [fileUploadKey, setFileUploadKey] = useState(0);
  const fileUploadRef = useRef();
  const [resumeUploaded, setResumeUploaded] = useState(false);
  const [coverLetterUploaded, setCoverLetterUploaded] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({ resumes: null });
  const [uploadedFilesList, setUploadedFilesList] = useState({ resumes: null });
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageUpload, setImageUpload] = useState(null);
  const [uploadedResume, setUploadedResume] = useState();
  const [uploadedCover, setUploadedCover] = useState();
  const [isEditing, setIsEditing] = useState(false);
  const fileInputRef = useRef(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [moduleExists, setModuleExists] = useState(true);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [language, setLanguage] = useState('EN'); // Will be set from fetched form data
  const { register } = useForm();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${apiUrl}authentication/categories-with-subcategories/`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('Categories with subcategories:', data);

      // Handle the nested structure
      if (data.categories) {
        setCategories(data.categories);
        console.log(`Found ${data.count} categories`);
      } else {
        // Fallback for simple structure
        setCategories(data);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    const category = categories.find(cat => cat.id === parseInt(categoryId));
    if (category) {
      setSubCategories(category.subcategories || []);
      // Reset subcategory selection when category changes
      setFormData(prevData => ({
        ...prevData,
        SubCategoryProfile: ''
      }));
    } else {
      setSubCategories([]);
    }
  };

  const CompanyInfo = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(3),
  }));

  const CompanyLogoContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    display: "flex",
    alignItems: "center",
  }));

  const CompanyLogo = styled(Box)(({ theme }) => ({
    width: 120,
    height: 120,
    borderRadius: "50%",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    border: "2px solid #fff",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  }));

  const LogoAvatar = styled(Avatar)(({ theme }) => ({
    width: "100%",
    height: "100%",
    fontSize: "16px",
    fontWeight: "bold",
  }));

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    fetchFormStructure();
    fetchAdminData();
    fetchCategories();
  }, []);

  useEffect(() => {
    // Check for admin module availability when adminData changes
    if (adminData) {
      setModuleExists(!!adminData);
      setError(adminData ? null : t("errors.moduleNotAvailable"));
    }
  }, [adminData, t]);

  // Update form structure when categories change
  useEffect(() => {
    if (categories.length > 0 && formStructure.length > 0) {
      const updatedStructure = formStructure.map(field => {
        if (field.name === 'CategoryProfile') {
          return {
            ...field,
            options: categories.map(category => ({
              value: category.id.toString(),
              label: language === 'FR' ? (category.name_fr || category.name) : category.name
            })),
            onChange: handleCategoryChange
          };
        }
        if (field.name === 'SubCategoryProfile') {
          const subcategoryOptions = subCategories.length > 0
            ? subCategories.map(subcategory => ({
                value: subcategory.id.toString(),
                label: language === 'FR' ? (subcategory.name_fr || subcategory.name) : subcategory.name
              }))
            : [{ value: "", label: language === 'FR' ? "Veuillez d'abord sélectionner une catégorie" : "Please select a category first" }];

          return {
            ...field,
            options: subcategoryOptions,
            disabled: !selectedCategory || subCategories.length === 0,
            required: selectedCategory && subCategories.length > 0
          };
        }
        return field;
      });
      setFormStructure(updatedStructure);
    }
  }, [categories, subCategories, selectedCategory, language]);

  const fetchFormStructure = async () => {
    try {
      const response = await axios.get(
        `${apiUrl}authentication/application-forms-by-company/`,
        {
          params: { company: companyName },
        }
      );
      if (response.data && response.data.length > 0) {
        console.log('response.data', response.data);
        setFormStructure(response.data[0].form_structure);
        setAdminId(response.data[0].admin);
        setDescription(response.data[0].description);
        // Set language from fetched form data
        if (response.data[0].language) {
          setLanguage(response.data[0].language);
        }
      } else {
        setError(t("errors.noFormStructure"));
      }
    } catch (error) {
      console.error(t("errors.errorFetchingForm"), error);
      setError(t("errors.failedToFetchForm"));
    }
  };

  const preset_key = process.env.REACT_APP_PRESET_KEY;
  const cloud_name = process.env.REACT_APP_CLOUD_NAME;
  function handleFile(event) {
    const file = event.target.files[0];
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", preset_key);
    axios
      .post(
        `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
        formData
      )
      .then((res) => {
        const imageUrl = res.data.secure_url;
        setImageUpload(imageUrl);
        setFormData((prev) => ({ ...prev, image: imageUrl }));
      })
      .catch((err) => console.log(err));
  }

  function handleImageClick() {
    fileInputRef.current.click();
  }

  const handleEdit = () => {
    setIsEditing(true);
  };

  const fetchAdminData = async () => {
    try {
      const response = await axios.get(
        `${apiUrl}authentication/get-admin-by-company/`,
        {
          params: { company: companyName },
        }
      );
      setAdminData(response.data);
      console.log("admin data", response.data);
    } catch (err) {
      console.error(t("errors.errorFetchingAdmin"), err);
      setError(t("errors.failedToFetchAdmin"));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleFileChange = (fieldName, uploadedFiles) => {
    console.log(`Files uploaded for ${fieldName}:`, uploadedFiles);

    // Store full file objects in `files` for later use in `FormData`
    setFiles((prevFiles) => ({
      ...prevFiles,
      [fieldName]: uploadedFiles,
    }));

    // Store only the file names in `formData`
    setFormData((prevData) => ({
      ...prevData,
      [fieldName]: uploadedFiles.map((file) => file.name),
    }));
  };

  const handleFileUpload = (event, fileType) => {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      if (fileType === "resume") {
        setResumeUploaded(true);
        setUploadedResume(file);
      } else if (fileType === "cover_letter") {
        setCoverLetterUploaded(true);
        setUploadedCover(file);
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    // Validate required CV upload
    if (!uploadedResume) {
      showSnackbar(t("messages.pleaseUploadResume"), "error");
      setSubmitting(false);
      return;
    }

    const formDataToSend = new FormData();

    // Add non-file fields to `form_data`
    const formDataObject = {};
    formDataToSend.append("admin", adminId);
    formDataToSend.append("image", imageUpload);
    Object.keys(formData).forEach((key) => {
      formDataObject[key] = formData[key];
    });
    formDataToSend.append("form_data", JSON.stringify(formDataObject));

    // Add CV (now guaranteed to exist)
    formDataToSend.append("cv", uploadedResume);

    // Add cover letter if uploaded
    if (uploadedCover) {
      formDataToSend.append("cover_letter", uploadedCover);
      console.log("cover to send:", uploadedCover);
    }

    console.log("FormData to send:", formDataToSend);

    try {
      const response = await axios.post(
        `${apiUrl}authentication/form-submissions/`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 201) {
        showSnackbar(t("success.formSaved"), "success");
        setFormData({});
        setFiles({});
        setFileUploadKey((prevKey) => prevKey + 1);
        if (fileUploadRef.current && fileUploadRef.current.removeFile) {
          fileUploadRef.current.removeFile();
        }
        setResumeUploaded(false);
        setCoverLetterUploaded(false);
        setUploadedResume(null);
        setUploadedCover(null);
      }
    } catch (err) {
      console.error(t("errors.errorSubmitting"), err);
      const errorMessage =
        err.response?.data?.error === "This email has already been submitted"
          ? t("messages.emailAlreadySubmitted")
          : t("messages.failedToSubmitForm");
      showSnackbar(errorMessage, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    switch (field.type) {
      case "text":
      case "email":
      case "number":
      case "date":
        if (field.name === "city") {
          return (
            <Grid item xs={12} sm={6} key={field.name}>
              <Typography
                sx={{
                  color: "#0000004D",
                  fontSize: "13px",
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                {field.label}
              </Typography>
              <GlobalCityAutocomplete
                value={formData[field.name] || ""}
                onChange={(newValue) =>
                  setFormData((prevData) => ({
                    ...prevData,
                    [field.name]: newValue,
                  }))
                }
                // label={field.label}
                required={field.required}
              />
            </Grid>
          );
        }
        return (
          <Grid item xs={12} sm={6} key={field.name}>
            <Typography
              sx={{
                color: "#0000004D",
                fontSize: "13px",
                fontFamily: "Poppins",
                fontWeight: 500,
                mb: 1,
              }}
            >
              {field.label}
            </Typography>
            <StyledTextField
              fullWidth
              // label={field.label}
              name={field.name}
              type={field.type}
              value={formData[field.name] || ""}
              onChange={(e) =>
                setFormData((prevData) => ({
                  ...prevData,
                  [field.name]: e.target.value,
                }))
              }
              variant="outlined"
              required={field.required}
              InputLabelProps={
                field.type === "date" ? { shrink: true } : undefined
              }
            />
          </Grid>
        );
      case "select":
        return (
          <Grid item xs={12} sm={6} key={field.name}>
            <FormControl fullWidth variant="outlined" required={field.required}>
              <Typography
                sx={{
                  color: "#0000004D",
                  fontSize: "13px",
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                {field.label}
              </Typography>
              <StyledSelect
                name={field.name}
                value={formData[field.name] || ""}
                onChange={(e) => {
                  setFormData((prevData) => ({
                    ...prevData,
                    [field.name]: e.target.value,
                  }));

                  // Handle category change to update subcategories
                  if (field.name === 'CategoryProfile' && field.onChange) {
                    field.onChange(e.target.value);
                  }
                }}
                disabled={field.disabled}
                // label={field.label}
              >
                {field.options.map((option) => (
                  <MenuItem
                    key={option.value || option}
                    value={option.value || option}
                  >
                    {option.label || option}
                  </MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </Grid>
        );
      case "radio":
        return (
          <Grid item xs={12} sm={6} key={field.name}>
            <FormControl component="fieldset" required={field.required}>
              <Typography
                sx={{
                  color: "#0000004D",
                  fontSize: "13px",
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                {field.label}
              </Typography>
              <RadioGroup
                name={field.name}
                value={formData[field.name] || ""}
                onChange={(e) =>
                  setFormData((prevData) => ({
                    ...prevData,
                    [field.name]: e.target.value,
                  }))
                }
                row
              >
                {field.options.map((option) => (
                  <FormControlLabel
                    key={option.value || option}
                    value={option.value || option}
                    control={<Radio />}
                    label={option.label || option}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>
        );
      case "textarea":
        return (
          <Grid item xs={12} sm={12} key={field.name}>
            <FormControl fullWidth variant="outlined" required={field.required}>
              <Typography
                sx={{
                  color: "#0000004D",
                  fontSize: "13px",
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                {field.label}
              </Typography>
              <TextField
                name={field.name}
                value={formData[field.name] || ""}
                onChange={(e) =>
                  setFormData((prevData) => ({
                    ...prevData,
                    [field.name]: e.target.value,
                  }))
                }
                multiline
                rows={4}
                variant="outlined"
              />
            </FormControl>
          </Grid>
        );

      case "checkbox":
        return (
          <Grid item xs={12} sm={6} key={field.name}>
            <FormControl component="fieldset" required={field.required}>
              <Typography
                sx={{
                  color: "#0000004D",
                  fontSize: "13px",
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                {field.label}
              </Typography>
              <FormGroup row>
                {field.options.map((option) => (
                  <FormControlLabel
                    key={option.value || option}
                    control={
                      <Checkbox
                        checked={
                          Array.isArray(formData[field.name])
                            ? formData[field.name]?.includes(
                                option.value || option
                              )
                            : false
                        }
                        onChange={(e) => {
                          const value = option.value || option;
                          setFormData((prevData) => {
                            const currentValues = Array.isArray(
                              prevData[field.name]
                            )
                              ? prevData[field.name]
                              : [];
                            return {
                              ...prevData,
                              [field.name]: e.target.checked
                                ? [...currentValues, value]
                                : currentValues.filter((v) => v !== value),
                            };
                          });
                        }}
                      />
                    }
                    label={option.label || option}
                  />
                ))}
              </FormGroup>
            </FormControl>
          </Grid>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress sx={{ color: "#333" }} />
      </Box>
    );
  }

  if (error) {
    return <ErrorDisplay message={error} />;
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {adminData && (
        <CompanyInfoCard elevation={0}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid
                item
                xs={12}
                md={4}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                <AvatarWrapper>
                  <StyledAvatar
                    src={adminData.company_details.company_logo}
                    alt={adminData.company_details.name}
                  />
                </AvatarWrapper>
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{ fontWeight: "bold", color: "#333" }}
                >
                  {adminData.company_details.name}
                </Typography>
                <InfoItem>
                  <LocationOnIcon />
                  <Typography variant="body1">
                    {adminData.company_details.address}, {adminData.company_details.city}, {adminData.company_details.country}
                  </Typography>
                </InfoItem>
                <InfoItem>
                  <BusinessIcon />
                  <Typography variant="body1">
                    {t("company.industry")}: {adminData.company_details.comp_industry}
                  </Typography>
                </InfoItem>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 2,
                    fontStyle: "italic",
                    color: "#555",
                    fontWeight: "bold",
                  }}
                >
                  {description}
                </Typography>
                <Divider sx={{ my: 2, backgroundColor: "#ccc" }} />
                {/* <InfoItem>
                  <PersonIcon />
                  <Typography variant="body2">
                    {t("company.admin")}: {adminData.first_name}{" "}
                    {adminData.last_name}
                  </Typography>
                </InfoItem> */}
                <InfoItem>
                  <EmailIcon />
                  <Typography variant="body2">
                    {t("company.contact")}: {adminData.email}
                  </Typography>
                </InfoItem>
              </Grid>
            </Grid>
          </CardContent>
        </CompanyInfoCard>
      )}

      <StyledCard>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <FormHeader>
              <FormSectionTitle>{t("applicationForm.title")}</FormSectionTitle>
              <LogoContainer onClick={() => navigate("/landing")}>
                <img src={new_logo} alt={t("navigation.smartHireLogo")} />
              </LogoContainer>
            </FormHeader>
            <CompanyInfo>
              <CompanyLogoContainer>
                <CompanyLogo>
                  <LogoAvatar src={formData.image} alt={formData.first_name} />
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFile}
                  />
                  <IconButton
                    onClick={handleImageClick}
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 0,
                      backgroundColor: "white",
                      "&:hover": { backgroundColor: "#f0f0f0" },
                    }}
                  >
                    <CameraIcon />
                  </IconButton>
                </CompanyLogo>
              </CompanyLogoContainer>
            </CompanyInfo>
            <Grid container spacing={3}>
              {formStructure
                .filter((field) => field.type !== "file")
                .map((field, index) => renderField(field))}
            </Grid>
            <Box sx={{ mt: 4 }}>
              <Grid container spacing={5} sx={{ paddingLeft: "3%" }}>
                <Grid
                  container
                  spacing={5}
                  sx={{ marginTop: "2%", paddingLeft: "3%" }}
                >
                  <FileUploadCard
                    type="resume"
                    isUploaded={resumeUploaded}
                    register={register}
                    handleFileUpload={(e) => handleFileUpload(e, "resume")}
                    required={true}
                  />
                  <FileUploadCard
                    type="cover_letter"
                    isUploaded={coverLetterUploaded}
                    register={register}
                    handleFileUpload={(e) =>
                      handleFileUpload(e, "cover_letter")
                    }
                    required={false}
                  />
                </Grid>
              </Grid>
            </Box>
            
            {/* Validation helper text */}
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography
                variant="body2"
                sx={{
                  color: !uploadedResume ? 'error.main' : 'text.secondary',
                  fontStyle: 'italic'
                }}
              >
                {!uploadedResume ? "CV/Resume upload is required to submit" : "All required files uploaded"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <OutlinedButton 
                type="submit" 
                disabled={submitting || !uploadedResume}
                sx={{
                  opacity: !uploadedResume ? 0.6 : 1,
                  cursor: !uploadedResume ? 'not-allowed' : 'pointer'
                }}
              >
                {submitting ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "10px",
                      alignItems: "center",
                    }}
                  >
                    <ModernLoader size={24} />
                    <Typography
                      sx={{
                        fontFamily: "Poppins",
                        fontWeight: 400,
                        fontSize: "14px",
                        lineHeight: "100%",
                        letterSpacing: "0%",
                      }}
                    >
                      {t("applicationPage.applyForOffer")}
                    </Typography>
                  </Box>
                ) : (
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "100%",
                      letterSpacing: "0%",
                    }}
                  >
                    {t("jobCard.applyNow")}
                  </Typography>
                )}
              </OutlinedButton>
            </Box>
          </form>
        </CardContent>
      </StyledCard>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        message={snackbar.message}
        sx={{
          "& .MuiSnackbarContent-root": {
            backgroundColor: "rgba(255, 255, 255, 0.3)",
            color: "black",
            borderRadius: "50px",
            backdropFilter: "blur(10px)",
            boxShadow: "0 4px 30px rgba(0, 0, 0, 0.4)",
          },
        }}
      />
    </Container>
  );
};

export default ApplicationFormSp;